/* eslint-disable camelcase */
import { FULFILLMENT_STATUS, MAINTENANCE_EVENT_STATUS } from 'src/constants/maintenance';
import { api, upload } from 'src/utils/api';
import { API_RESOURCES, WORK_INSTRUCTION_DOCUMENT_TYPES } from 'src/utils/constants';
import { serializeReportValue } from 'src/utils/maintenance';
import { getResourceUri, getUuid } from 'src/utils/url';

const locationUuid = response => getUuid(response.headers.get('location'));

const getByUuid = (resource, uuid) => api.get(`${resource}/${uuid}/`).json();

const applyWriteThroughFields = (resource, payload, fields) =>
  fields.reduce(
    (next, field) =>
      Object.prototype.hasOwnProperty.call(payload, field)
        ? { ...next, [field]: payload[field] }
        : next,
    resource
  );

export const createEvent = async ({ targetUri, notes }, onErrorToastCallback) => {
  const json = { target_uri: targetUri };
  if (notes) json.notes = notes;
  const response = await api.post(`${API_RESOURCES.MAINTENANCE_EVENT}/`, {
    json,
    onErrorToastCallback,
  });
  return getByUuid(API_RESOURCES.MAINTENANCE_EVENT, locationUuid(response));
};

export const updateEvent = async (uuid, payload, onErrorToastCallback) => {
  await api.put(`${API_RESOURCES.MAINTENANCE_EVENT}/${uuid}/`, {
    json: payload,
    onErrorToastCallback,
  });
  const event = await getByUuid(API_RESOURCES.MAINTENANCE_EVENT, uuid);
  return applyWriteThroughFields(event, payload, ['status', 'downtime', 'notes']);
};

export const deleteEvent = (uuid, onErrorToastCallback) =>
  api.delete(`${API_RESOURCES.MAINTENANCE_EVENT}/${uuid}/`, { onErrorToastCallback });

/**
 * Start a planned event (created → in_progress). Downtime is optional and only
 * settable here: create a new one, link an existing one, or neither. A planned
 * event that already has a linked downtime must start with none of these.
 */
export const startEvent = async (uuid, downtime, onErrorToastCallback) => {
  const payload = { status: MAINTENANCE_EVENT_STATUS.IN_PROGRESS };
  if (downtime?.create) {
    const { assignedUser, ...fields } = downtime.create;
    payload.create_downtime = true;
    payload.downtime_create = { ...fields }; // { description, start, finish }
    if (assignedUser) payload.downtime_create.assigned_user = assignedUser;
  } else if (downtime?.existingUri) {
    payload.downtime = downtime.existingUri;
  }
  return updateEvent(uuid, payload, onErrorToastCallback);
};

/** Complete an in-progress event. `freeMachineNow` closes the linked downtime now. */
export const completeEvent = (uuid, freeMachineNow, onErrorToastCallback) =>
  updateEvent(
    uuid,
    { status: MAINTENANCE_EVENT_STATUS.COMPLETE, close_downtime: Boolean(freeMachineNow) },
    onErrorToastCallback
  );

/**
 * Cancel an event. A planned (created) event is DELETEd (the backend rejects
 * created → cancelled); an in-progress event is PUT to cancelled (the server
 * auto-skips its open fulfillments - no client cascade).
 */
export const cancelEvent = async (event, freeMachineNow, onErrorToastCallback) => {
  const eventUuid = getUuid(event.uri);
  if (event.status === MAINTENANCE_EVENT_STATUS.PLANNED) {
    await deleteEvent(eventUuid, onErrorToastCallback);
    return null;
  }
  return updateEvent(
    eventUuid,
    { status: MAINTENANCE_EVENT_STATUS.CANCELLED, close_downtime: Boolean(freeMachineNow) },
    onErrorToastCallback
  );
};

export const linkDowntime = (eventUuid, downtimeUri, onErrorToastCallback) =>
  updateEvent(eventUuid, { downtime: downtimeUri }, onErrorToastCallback);

export const unlinkDowntime = (eventUuid, onErrorToastCallback) =>
  updateEvent(eventUuid, { downtime: null }, onErrorToastCallback);

/** Create a standalone downtime window on a workstation (then link it to an event). */
export const createDowntime = async (
  { description, start, finish, assignedUser, workstationField, workstationUri },
  onErrorToastCallback
) => {
  const json = { description, start, finish, [workstationField]: workstationUri };
  if (assignedUser) json.assigned_user = assignedUser;
  const response = await api.post(`${API_RESOURCES.DOWNTIME}/`, { json, onErrorToastCallback });
  return getByUuid(API_RESOURCES.DOWNTIME, locationUuid(response));
};

export const createFulfillment = async ({ eventUri, requirementUri }, onErrorToastCallback) => {
  const response = await api.post(`${API_RESOURCES.MAINTENANCE_EVENT_REQUIREMENT}/`, {
    json: { maintenance_event: eventUri, maintenance_requirement: requirementUri },
    onErrorToastCallback,
  });
  return getByUuid(API_RESOURCES.MAINTENANCE_EVENT_REQUIREMENT, locationUuid(response));
};

export const updateFulfillment = async (uuid, payload, onErrorToastCallback) => {
  await api.put(`${API_RESOURCES.MAINTENANCE_EVENT_REQUIREMENT}/${uuid}/`, {
    json: payload,
    onErrorToastCallback,
  });
  const fulfillment = await getByUuid(API_RESOURCES.MAINTENANCE_EVENT_REQUIREMENT, uuid);
  return applyWriteThroughFields(fulfillment, payload, ['status', 'notes']);
};

export const deleteFulfillment = (uuid, onErrorToastCallback) =>
  api.delete(`${API_RESOURCES.MAINTENANCE_EVENT_REQUIREMENT}/${uuid}/`, { onErrorToastCallback });

/** Complete a fulfillment: selected → in_progress → complete (two hops required). */
export const completeFulfillment = async (uuid, onErrorToastCallback) => {
  await api.put(`${API_RESOURCES.MAINTENANCE_EVENT_REQUIREMENT}/${uuid}/`, {
    json: { status: FULFILLMENT_STATUS.IN_PROGRESS },
    onErrorToastCallback,
  });
  return updateFulfillment(uuid, { status: FULFILLMENT_STATUS.COMPLETE }, onErrorToastCallback);
};

export const skipFulfillment = (uuid, notes, onErrorToastCallback) =>
  updateFulfillment(
    uuid,
    { status: FULFILLMENT_STATUS.SKIPPED, ...(notes ? { notes } : {}) },
    onErrorToastCallback
  );

/**
 * Save one instruction answer for a fulfillment.
 * - Non-file types: POST (create) or PUT (update) with the serialized `report`.
 * - Picture/document: create a report shell, POST a linked document, upload the
 *   bytes, then PUT the report with the document uuid.
 * Returns the report uuid so the caller can track/update it.
 */
export const saveInstructionReport = async (
  { fulfillmentUri, instruction, value, reportUuid, file, onUploadProgress, onReportCreated },
  onErrorToastCallback
) => {
  const isFile = WORK_INSTRUCTION_DOCUMENT_TYPES.includes(instruction.reportType);

  // Ensure a report row exists (unique per work_instruction + fulfillment).
  let uuid = reportUuid;
  if (!uuid) {
    const json = {
      work_instruction: instruction.uri,
      maintenance_event_requirement_fulfillment: fulfillmentUri,
    };
    if (!isFile) {
      const report = serializeReportValue(instruction.reportType, value);
      if (report !== undefined) json.report = report;
    }
    const response = await api.post(`${API_RESOURCES.MAINTENANCE_INSTRUCTION_REPORT}/`, {
      json,
      onErrorToastCallback,
    });
    uuid = locationUuid(response);
    // Surface the new report uuid immediately so a later upload failure is still
    // retryable — the retry PUTs to this shell instead of POSTing a duplicate that
    // would violate the unique (instruction, fulfillment) constraint.
    if (onReportCreated) onReportCreated(uuid);
  } else if (!isFile) {
    const report = serializeReportValue(instruction.reportType, value);
    if (report !== undefined) {
      await api.put(`${API_RESOURCES.MAINTENANCE_INSTRUCTION_REPORT}/${uuid}/`, {
        json: { report },
        onErrorToastCallback,
      });
    }
  }

  let reportFile = null;
  if (isFile && file) {
    const documentUuid = await uploadReportDocument(
      {
        reportUuid: uuid,
        file,
        onUploadProgress,
        parserTemplate: instruction.parserTemplate,
        autoParse: false,
      },
      onErrorToastCallback
    );
    await api.put(`${API_RESOURCES.MAINTENANCE_INSTRUCTION_REPORT}/${uuid}/`, {
      json: { report: documentUuid },
      onErrorToastCallback,
    });
    // Hand back a viewer-ready descriptor (same shape as GET's report_file).
    reportFile = { uri: getResourceUri(API_RESOURCES.DOCUMENT, documentUuid), filename: file.name };
  }

  return { uuid, reportFile };
};

/** Remove a captured report (e.g. clearing a non-required attachment). */
export const deleteInstructionReport = (uuid, onErrorToastCallback) =>
  api.delete(`${API_RESOURCES.MAINTENANCE_INSTRUCTION_REPORT}/${uuid}/`, { onErrorToastCallback });

/** Create a document linked to a maintenance instruction report and upload the bytes. */
export const uploadReportDocument = async (
  { reportUuid, file, onUploadProgress, parserTemplate, autoParse = false },
  onErrorToastCallback
) => {
  const documentResponse = await api.post(`${API_RESOURCES.DOCUMENT}/`, {
    json: {
      name: file.name,
      related_resources: [
        { related_uuid: reportUuid, related_table_name: 'maintenance_instruction_report' },
      ],
      ...(parserTemplate ? { parser_template: parserTemplate, auto_parse: autoParse } : {}),
    },
    onErrorToastCallback,
  });
  const documentUuid = locationUuid(documentResponse);
  const uploadLocation = documentResponse.headers
    .get('x-upload-location')
    ?.replace(/^http:/, 'https:');
  await upload(uploadLocation, {
    contentType: file.type || 'application/octet-stream',
    body: file,
    onUploadProgress: onUploadProgress || (() => {}),
    onErrorToastCallback,
  });
  return documentUuid;
};

export const getMaintenanceDue = async equipmentUri => {
  const response = await api
    .get(`${API_RESOURCES.MAINTENANCE_DUE}/`, {
      searchParams: { 'filter[equipment_uri]': equipmentUri },
    })
    .json();

  return response.resources || [];
};
